import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentApi } from '../api/payment';
import { formatCurrencyFromMinor } from '../utils/currency';
import { getStripePromise } from '../utils/stripeKey';
import './booking/BookingMagicLinkPaymentPage.css';
import './TipPaymentPage.css';

/**
 * Thank-you card, shown after paying and to anyone reopening a paid link.
 */
const TipThanks = () => (
    <div className="booking-payment-success-card">
        <h2>Thank you!</h2>
        <p>Your gift helps us keep Dance Localé growing.</p>
        <p style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--dl-color-medium)' }}>
            You can <strong>close</strong> this window.
        </p>
    </div>
);

/**
 * TipForm — must be a child of <Elements> so Stripe hooks work. Carries the same
 * mount-gate + try/catch + onLoadError safety pattern as every other payment form
 * (see dance-locale-web/CLAUDE.md "Every Stripe Payment Form Needs the Mount-Gate").
 */
const TipForm = ({ tip }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaying, setIsPaying] = useState(false);
    const [isPaid, setIsPaid] = useState(false);
    const [elementsReady, setElementsReady] = useState(false);

    const handlePay = async () => {
        if (!stripe || !elements || !elementsReady) return;
        setIsPaying(true);
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: 'if_required',
            });

            if (error) {
                toast.error(error.message || 'Payment failed. Please try again.');
                return;
            }

            if (paymentIntent?.status === 'succeeded') {
                setIsPaid(true);
            } else {
                toast.error('Payment not completed. Please try again.');
            }
        } catch {
            toast.error('Payment failed. Please try again.');
        } finally {
            setIsPaying(false);
        }
    };

    if (isPaid) return <TipThanks />;

    return (
        <>
            <div className="booking-payment-total">
                <span>Your gift</span>
                <span className="booking-payment-amount">{formatCurrencyFromMinor(tip.amount, tip.currency)}</span>
            </div>

            <div className="booking-payment-card-element">
                <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => toast.error(e?.error?.message || 'Could not load the payment form. Please try again.')} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card'] }} />
            </div>

            <button
                className="booking-payment-btn"
                onClick={handlePay}
                disabled={isPaying || !stripe || !elements || !elementsReady}>
                {isPaying ? 'Processing...' : `Give ${formatCurrencyFromMinor(tip.amount, tip.currency)}`}
            </button>

            <p className="booking-payment-secure-note">
                Secured by Stripe. Your card details are never stored.
            </p>
        </>
    );
};

/**
 * TipPaymentPage — public payment page for a gift to Dance Localé ("tip" in the backend).
 * Route: /tip/:token. The token (from POST /api/v2/payment/tip) is the credential,
 * like the booking pay links; the app opens it in the in-app browser.
 */
const TipPaymentPage = () => {
    const { token } = useParams();
    const [tip, setTip] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadTip = async () => {
            try {
                setTip(await paymentApi.getTip(token));
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        loadTip();
    }, [token]);

    if (isLoading) {
        return (
            <div className="booking-payment-page tip-payment-page">
                <div className="booking-payment-loading">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="booking-payment-page tip-payment-page">
                <div className="booking-payment-error-card">
                    <h2>Gift Link Invalid</h2>
                    <p>{error}</p>
                    <p>This link may have expired. Please go back and choose an amount again.</p>
                </div>
            </div>
        );
    }

    // Stripe's iframe cannot read CSS variables, so resolve the brand purple first.
    const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--dl-color-purple-default').trim() || '#451f78';

    return (
        <div className="booking-payment-page tip-payment-page">
            <div className="booking-payment-header">
                <h1 className="booking-payment-org-name">Help us out</h1>
                <p className="booking-payment-subtitle">A gift to support Dance Localé</p>
            </div>

            <div className="booking-payment-card">
                {tip.status === 'succeeded' ? (
                    <TipThanks />
                ) : (
                    <Elements stripe={getStripePromise()} options={{
                        clientSecret: tip.clientSecret,
                        appearance: {
                            theme: 'stripe',
                            variables: {
                                colorPrimary: primaryColor,
                                colorBackground: '#ffffff',
                                colorText: '#1a1a1a',
                                borderRadius: '8px',
                                fontFamily: 'system-ui, sans-serif',
                            },
                        },
                    }}>
                        <TipForm tip={tip} />
                    </Elements>
                )}
            </div>
        </div>
    );
};

export default TipPaymentPage;
